import React, { useState, useEffect, useCallback } from "react";
import {
  FaHistory,
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaShieldAlt,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaUser,
  FaClock,
  FaDesktop,
  FaGlobe,
  FaSyncAlt,
} from "react-icons/fa";
import { getAdminActivityLogs, getAdminActivityStats } from "../firebase";
import "./AdminActivityLogs.css";

const AdminActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    adminEmail: "",
    action: "",
    startDate: "",
    endDate: "",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
  });

  // Action type mappings for better display (only critical actions)
  const actionMappings = {
    admin_login: { label: "Admin Girişi", icon: FaShieldAlt, color: "#27ae60" },
    admin_logout: {
      label: "Admin Çıkışı",
      icon: FaTimesCircle,
      color: "#e74c3c",
    },
    session_timeout: {
      label: "Oturum Zaman Aşımı",
      icon: FaClock,
      color: "#f39c12",
    },
    invalid_session_access: {
      label: "Geçersiz Oturum Erişimi",
      icon: FaExclamationTriangle,
      color: "#e74c3c",
    },
    appointment_status_change: {
      label: "Randevu Durumu Değişikliği",
      icon: FaCalendarAlt,
      color: "#9b59b6",
    },
    appointment_deletion: {
      label: "Randevu Silme",
      icon: FaTimesCircle,
      color: "#e74c3c",
    },
    student_data_modification: {
      label: "Öğrenci Verisi Güncelleme",
      icon: FaUser,
      color: "#f39c12",
    },
    student_deletion: {
      label: "Öğrenci Silme",
      icon: FaTimesCircle,
      color: "#e74c3c",
    },
    availability_settings_change: {
      label: "Uygunluk Ayarları Değişikliği",
      icon: FaCalendarAlt,
      color: "#3498db",
    },
    fomo_settings_change: {
      label: "FOMO Ayarları Değişikliği",
      icon: FaInfoCircle,
      color: "#9b59b6",
    },
    mailing_list_modification: {
      label: "Mail Listesi Değişikliği",
      icon: FaUser,
      color: "#f39c12",
    },
    admin_settings_change: {
      label: "Admin Ayarları Değişikliği",
      icon: FaShieldAlt,
      color: "#e74c3c",
    },
    bulk_operations: {
      label: "Toplu İşlemler",
      icon: FaDownload,
      color: "#f39c12",
    },
    data_export: {
      label: "Veri Dışa Aktarma",
      icon: FaDownload,
      color: "#3498db",
    },
    security_incident: {
      label: "Güvenlik Olayı",
      icon: FaExclamationTriangle,
      color: "#e74c3c",
    },
    "2fa_login_failed": {
      label: "2FA Giriş Başarısız",
      icon: FaExclamationTriangle,
      color: "#e74c3c",
    },
  };

  // Severity colors
  const severityColors = {
    low: "#95a5a6",
    medium: "#f39c12",
    high: "#e67e22",
    critical: "#e74c3c",
  };

  const fetchLogs = useCallback(
    async (newFilters = filters) => {
      try {
        setLoading(true);
        const result = await getAdminActivityLogs(newFilters);

        if (newFilters.offset === 0) {
          setLogs(result.logs);
        } else {
          setLogs((prev) => [...prev, ...result.logs]);
        }

        setPagination(result.pagination);
      } catch (error) {
        console.error("Error fetching logs:", error);
        console.error("Error details:", error.message);
        alert(
          `Loglar yüklenirken hata oluştu: ${
            error.message || "Bilinmeyen hata"
          }`
        );
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const fetchStats = useCallback(async () => {
    try {
      const result = await getAdminActivityStats(30);
      setStats(result.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
      console.error("Stats error details:", error.message);
      // Don't show alert for stats error, just log it
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset pagination when filters change
    }));
  };

  const applyFilters = () => {
    const newFilters = { ...filters, offset: 0 };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const loadMore = () => {
    const newFilters = {
      ...filters,
      offset: filters.offset + filters.limit,
    };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      limit: 50,
      offset: 0,
      adminEmail: "",
      action: "",
      startDate: "",
      endDate: "",
    };
    setFilters(defaultFilters);
    fetchLogs(defaultFilters);
  };

  const exportLogs = () => {
    const csvContent = [
      [
        "Tarih",
        "Admin",
        "İşlem",
        "Önem",
        "Tarayıcı",
        "Platform",
        "Detaylar",
      ].join(","),
      ...logs.map((log) =>
        [
          new Date(log.timestamp).toLocaleString("tr-TR"),
          log.adminEmail,
          actionMappings[log.action]?.label || log.action,
          log.severity || "low",
          log.sessionInfo?.browser || "Unknown",
          log.sessionInfo?.platform || "Unknown",
          JSON.stringify(log.details).replace(/"/g, '""'),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-activity-logs-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString("tr-TR"),
      time: date.toLocaleTimeString("tr-TR"),
      relative: getRelativeTime(date),
    };
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Az önce";
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString("tr-TR");
  };

  return (
    <div className="admin-activity-logs">
      <div className="activity-header">
        <div className="header-content">
          <div className="header-title">
            <FaHistory className="header-icon" />
            <h2>Admin Aktivite Logları</h2>
          </div>
          <div className="header-actions">
            <button
              className="refresh-btn"
              onClick={() => fetchLogs()}
              disabled={loading}
            >
              <FaSyncAlt /> Yenile
            </button>
            <button
              className="filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filtreler
            </button>
            <button className="export-btn" onClick={exportLogs}>
              <FaDownload /> Dışa Aktar
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats.totalActivities}</div>
              <div className="stat-label">Toplam Aktivite</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {Object.keys(stats.adminStats).length}
              </div>
              <div className="stat-label">Aktif Admin</div>
            </div>
            <div className="stat-card critical">
              <div className="stat-number">{stats.severityStats.critical}</div>
              <div className="stat-label">Kritik İşlemler</div>
            </div>
            <div className="stat-card high">
              <div className="stat-number">{stats.severityStats.high}</div>
              <div className="stat-label">Yüksek Önemli</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Admin E-posta:</label>
              <input
                type="text"
                value={filters.adminEmail}
                onChange={(e) =>
                  handleFilterChange("adminEmail", e.target.value)
                }
                placeholder="admin@example.com"
              />
            </div>
            <div className="filter-group">
              <label>İşlem Türü:</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange("action", e.target.value)}
              >
                <option value="">Tümü</option>
                {Object.entries(actionMappings).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Başlangıç Tarihi:</label>
              <input
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
              />
            </div>
            <div className="filter-group">
              <label>Bitiş Tarihi:</label>
              <input
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
          </div>
          <div className="filters-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              <FaSearch /> Filtrele
            </button>
            <button className="reset-filters-btn" onClick={resetFilters}>
              Sıfırla
            </button>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="logs-container">
        {loading && logs.length === 0 ? (
          <div className="loading">Loglar yükleniyor...</div>
        ) : logs.length === 0 ? (
          <div className="no-logs">
            <FaHistory size={48} />
            <p>Gösterilecek log bulunamadı.</p>
          </div>
        ) : (
          <>
            <div className="logs-list">
              {logs.map((log) => {
                const action = actionMappings[log.action] || {
                  label: log.action,
                  icon: FaInfoCircle,
                  color: "#95a5a6",
                };
                const IconComponent = action.icon;
                const timestamp = formatTimestamp(log.timestamp);

                return (
                  <div
                    key={log.id}
                    className={`log-item severity-${log.severity || "low"}`}
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="log-icon" style={{ color: action.color }}>
                      <IconComponent />
                    </div>
                    <div className="log-content">
                      <div className="log-main">
                        <div className="log-action">{action.label}</div>
                        <div className="log-admin">{log.adminEmail}</div>
                      </div>
                      <div className="log-meta">
                        <span
                          className="log-time"
                          title={`${timestamp.date} ${timestamp.time}`}
                        >
                          <FaClock /> {timestamp.relative}
                        </span>
                        <span className="log-browser">
                          <FaDesktop /> {log.sessionInfo?.browser || "Unknown"}
                        </span>
                        <span className="log-platform">
                          <FaGlobe /> {log.sessionInfo?.platform || "Unknown"}
                        </span>
                        <span
                          className={`log-severity severity-${
                            log.severity || "low"
                          }`}
                          style={{
                            color: severityColors[log.severity || "low"],
                          }}
                        >
                          {(log.severity || "low").toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button */}
            {pagination.hasMore && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? "Yükleniyor..." : "Daha Fazla Yükle"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="log-modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="log-modal" onClick={(e) => e.stopPropagation()}>
            <div className="log-modal-header">
              <h3>Log Detayları</h3>
              <button
                className="close-modal-btn"
                onClick={() => setSelectedLog(null)}
              >
                ✕
              </button>
            </div>
            <div className="log-modal-content">
              <div className="log-detail-grid">
                <div className="detail-item">
                  <strong>İşlem:</strong>
                  <span>
                    {actionMappings[selectedLog.action]?.label ||
                      selectedLog.action}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Admin:</strong>
                  <span>{selectedLog.adminEmail}</span>
                </div>
                <div className="detail-item">
                  <strong>Tarih/Saat:</strong>
                  <span>
                    {formatTimestamp(selectedLog.timestamp).date}{" "}
                    {formatTimestamp(selectedLog.timestamp).time}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Önem Seviyesi:</strong>
                  <span
                    className={`severity-badge severity-${
                      selectedLog.severity || "low"
                    }`}
                    style={{
                      color: severityColors[selectedLog.severity || "low"],
                    }}
                  >
                    {(selectedLog.severity || "low").toUpperCase()}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Tarayıcı:</strong>
                  <span>{selectedLog.sessionInfo?.browser || "Unknown"}</span>
                </div>
                <div className="detail-item">
                  <strong>Platform:</strong>
                  <span>{selectedLog.sessionInfo?.platform || "Unknown"}</span>
                </div>
                <div className="detail-item">
                  <strong>IP Adresi:</strong>
                  <span>{selectedLog.ipAddress || "Unknown"}</span>
                </div>
              </div>

              {selectedLog.details &&
                Object.keys(selectedLog.details).length > 0 && (
                  <div className="log-details-section">
                    <h4>Ek Detaylar:</h4>
                    <pre className="details-json">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityLogs;
